import React, { useState, useRef, useCallback } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ActionIcon } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import apiClient from './services/apiClient';

const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDateTimeParam = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const date = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
    const time = `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
    const offset = -d.getTimezoneOffset();
    const sign = offset >= 0 ? '+' : '-';
    const abs = Math.abs(offset);
    const tz = `${sign}${pad(Math.floor(abs / 60))}${pad(abs % 60)}`;
    return `${date}T${time}${tz}`;
};

const formatPlate = (v = '') => {
    const clean = v.replace(/[^A-Z0-9]/g, '');
    return clean.length === 6 ? `${clean.slice(0, 3)}-${clean.slice(3)}` : clean;
};

const FUEL_TYPES = ['Petrol', 'Diesel', 'LPG', 'Premium', 'E10', 'Other'];

let idCounter = 0;
const makeEntry = (file, preview, prefilledPlate = '') => ({
    id: ++idCounter,
    file,
    preview,
    plate: prefilledPlate,
    date: today(),
    amount: '',
    fuelType: 'Petrol',
    pumpNumber: '',
    confirmed: false,
    submitted: false,
});

const fieldStyle = {
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #E2E8F0',
    borderRadius: '12px',
    outline: 'none',
    boxSizing: 'border-box',
    width: '100%',
    backgroundColor: 'white',
    color: '#334155',
};

const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#334155',
    marginBottom: '6px',
};

const ManualEntryPage = () => {
    const { user, getAccessTokenSilently } = useAuth0();
    const [searchParams] = useSearchParams();
    const dropRef = useRef(null);
    const fileInputRef = useRef(null);
    const prefilledPlateFromQuery = (searchParams.get('plate') || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

    const [entries, setEntries] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sessionCount, setSessionCount] = useState(0);
    const [lightboxSrc, setLightboxSrc] = useState(null);

    const addFiles = useCallback((files) => {
        const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
        if (!imageFiles.length) return;
        imageFiles.forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setEntries(prev => [...prev, makeEntry(file, reader.result, prefilledPlateFromQuery)]);
            };
            reader.readAsDataURL(file);
        });
    }, [prefilledPlateFromQuery]);

    const onDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    }, [addFiles]);

    const onDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const onDragLeave = () => setIsDragging(false);

    const updateEntry = (id, field, value) => {
        setEntries(prev => prev.map(e => e.id === id ? { ...e, [field]: value, confirmed: false } : e));
    };

    const toggleConfirm = (id) => {
        setEntries(prev => prev.map(e => {
            if (e.id !== id) return e;
            if (!e.plate || !e.amount || !e.date) {
                toast.warning('Please fill in plate, date and amount before confirming.', { icon: '⚠️' });
                return e;
            }
            return { ...e, confirmed: !e.confirmed };
        }));
    };

    const removeEntry = (id) => {
        setEntries(prev => prev.filter(e => e.id !== id));
    };

    const confirmedEntries = entries.filter(e => e.confirmed && !e.submitted);
    const canSubmit = confirmedEntries.length > 0 && !isSubmitting;

    const handleSubmitAll = async () => {
        if (!canSubmit) return;
        setIsSubmitting(true);

        const accessToken = await getAccessTokenSilently({
            authorizationParams: { audience: process.env.REACT_APP_AUTH0_API_AUDIENCE },
        });

        let submitted = 0;
        for (const entry of confirmedEntries) {
            try {
                const cleanPlate = entry.plate.replace(/-/g, '').toUpperCase();
                const queryParams = {
                    channelID: 1,
                    dateTime: formatDateTimeParam(),
                    eventType: 'vehicleDetection',
                    licensePlate: cleanPlate,
                    lane: 1,
                    direction: 'forward',
                    confidenceLevel: 100,
                };
                await apiClient.postManualDetectionEntry(queryParams, { image1: entry.file }, accessToken);
                setEntries(prev => prev.map(e => e.id === entry.id ? { ...e, submitted: true } : e));
                submitted++;
            } catch (err) {
                toast.error(`Failed to submit ${entry.plate || 'entry'} — ${err.message}`, { icon: '❌' });
                console.error('Error submitting entry', entry.id, err);
            }
        }

        setSessionCount(c => c + submitted);
        setIsSubmitting(false);

        if (submitted > 0) {
            toast.success(`${submitted} drive-off receipt${submitted > 1 ? 's' : ''} submitted`, { icon: '✅' });
            // Remove submitted entries after a short delay so user sees the tick
            setTimeout(() => {
                setEntries(prev => prev.filter(e => !e.submitted));
            }, 1200);
        }
    };

    const pendingCount = entries.filter(e => !e.submitted).length;

    return (
        <>
        {/* Lightbox overlay */}
        {lightboxSrc && (
            <div
                onClick={() => setLightboxSrc(null)}
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 9999,
                    backgroundColor: 'rgba(0,0,0,0.85)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'zoom-out',
                }}
            >
                <button
                    onClick={() => setLightboxSrc(null)}
                    style={{
                        position: 'absolute',
                        top: '20px',
                        right: '24px',
                        background: 'rgba(255,255,255,0.15)',
                        border: 'none',
                        color: 'white',
                        fontSize: '24px',
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                    }}
                >
                    ✕
                </button>
                <img
                    src={lightboxSrc}
                    alt="Receipt"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        objectFit: 'contain',
                        borderRadius: '12px',
                        boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
                        cursor: 'default',
                    }}
                />
            </div>
        )}

        <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>

            {/* Page header — sits below the main navbar (which is fixed-top ~72px) */}
            <div style={{ paddingTop: '80px' }}>
                <div style={{ width: '100%', maxWidth: '1100px', margin: '0', padding: '24px 32px', textAlign: 'left' }}>

                    {/* Title row */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px' }}>
                        <div>
                            <h1 style={{ margin: 0, fontWeight: '700', fontSize: '30px', lineHeight: 1.2, color: '#111827' }}>
                                Add Drive Off
                            </h1>
                            <p style={{ margin: 0, fontSize: '15px', color: '#6b7280', marginTop: '8px', lineHeight: 1.6 }}>
                                Use this page to log existing drive-offs into the system. Simply photograph the drive off receipt with the registration number also visible on the receipt — handwritten registration numbers are fine — and we'll extract and record the details automatically.
                            </p>
                        </div>
                        {sessionCount > 0 && (
                            <span style={{
                                backgroundColor: 'rgba(34,197,94,0.12)',
                                color: '#15803d',
                                border: '1px solid rgba(34,197,94,0.3)',
                                borderRadius: '20px',
                                padding: '4px 14px',
                                fontSize: '13px',
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                                marginLeft: '16px',
                            }}>
                                {sessionCount} submitted this session
                            </span>
                        )}
                    </div>

                    {/* Drop zone */}
                    <div
                        ref={dropRef}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        onDragLeave={onDragLeave}
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                            border: `2px dashed ${isDragging ? 'var(--brand-blue)' : '#d1d5db'}`,
                            borderRadius: '16px',
                            backgroundColor: isDragging ? 'rgba(31,143,212,0.04)' : 'white',
                            padding: '40px 24px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            marginBottom: '24px',
                        }}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            style={{ display: 'none' }}
                            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                        />
                        <div style={{ fontSize: '36px', marginBottom: '10px' }}>🧾</div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                            {isDragging ? 'Drop receipts here' : 'Upload receipt photos'}
                        </div>
                        <div style={{ fontSize: '13px', color: '#9ca3af' }}>
                            Drag & drop or click to select — multiple files supported
                        </div>
                    </div>

                    {/* Review list */}
                    {entries.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                            {entries.map(entry => (
                                <div
                                    key={entry.id}
                                    style={{
                                        backgroundColor: 'white',
                                        border: `1px solid ${entry.submitted ? '#6ee7b7' : entry.confirmed ? '#26A5DF' : '#E2E8F0'}`,
                                        borderRadius: '12px',
                                        padding: '20px',
                                        opacity: entry.submitted ? 0.6 : 1,
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>

                                        {/* Receipt thumbnail */}
                                        <div style={{ flexShrink: 0 }}>
                                            <img
                                                src={entry.preview}
                                                alt="Receipt"
                                                onClick={() => setLightboxSrc(entry.preview)}
                                                style={{
                                                    width: '80px',
                                                    height: '100px',
                                                    objectFit: 'cover',
                                                    borderRadius: '8px',
                                                    border: '1px solid #e5e7eb',
                                                    display: 'block',
                                                    cursor: 'zoom-in',
                                                }}
                                            />
                                        </div>

                                        {/* Fields */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            {/* Row 1: Plate + Date + Amount */}
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                                                <div>
                                                    <label style={labelStyle}>Plate *</label>
                                                    <input
                                                        type="text"
                                                        value={formatPlate(entry.plate)}
                                                        onChange={(e) => updateEntry(entry.id, 'plate', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                                                        placeholder="ABC123"
                                                        maxLength={7}
                                                        disabled={entry.submitted}
                                                        style={{
                                                            ...fieldStyle,
                                                            fontWeight: '600',
                                                            letterSpacing: '0.04em',
                                                            textTransform: 'uppercase',
                                                        }}
                                                        onFocus={(e) => e.target.style.borderColor = 'var(--brand-blue)'}
                                                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                                    />
                                                </div>
                                                <div>
                                                    <label style={labelStyle}>Date *</label>
                                                    <input
                                                        type="date"
                                                        value={entry.date}
                                                        onChange={(e) => updateEntry(entry.id, 'date', e.target.value)}
                                                        disabled={entry.submitted}
                                                        style={fieldStyle}
                                                        onFocus={(e) => e.target.style.borderColor = 'var(--brand-blue)'}
                                                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                                    />
                                                </div>
                                                <div>
                                                    <label style={labelStyle}>Amount ($) *</label>
                                                    <input
                                                        type="number"
                                                        value={entry.amount}
                                                        onChange={(e) => updateEntry(entry.id, 'amount', e.target.value)}
                                                        placeholder="85.50"
                                                        min="0"
                                                        step="0.01"
                                                        disabled={entry.submitted}
                                                        style={{ ...fieldStyle, MozAppearance: 'textfield', appearance: 'textfield' }}
                                                        onFocus={(e) => e.target.style.borderColor = 'var(--brand-blue)'}
                                                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                                        onWheel={(e) => e.target.blur()}
                                                    />
                                                </div>
                                            </div>

                                            {/* Row 2: Fuel + Pump */}
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                <div>
                                                    <label style={labelStyle}>Fuel Type</label>
                                                    <select
                                                        value={entry.fuelType}
                                                        onChange={(e) => updateEntry(entry.id, 'fuelType', e.target.value)}
                                                        disabled={entry.submitted}
                                                        style={fieldStyle}
                                                        onFocus={(e) => e.target.style.borderColor = 'var(--brand-blue)'}
                                                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                                    >
                                                        {FUEL_TYPES.map(f => <option key={f}>{f}</option>)}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label style={labelStyle}>Pump No.</label>
                                                    <input
                                                        type="number"
                                                        value={entry.pumpNumber}
                                                        onChange={(e) => updateEntry(entry.id, 'pumpNumber', e.target.value)}
                                                        placeholder="e.g. 3"
                                                        min="1"
                                                        disabled={entry.submitted}
                                                        style={{ ...fieldStyle, MozAppearance: 'textfield', appearance: 'textfield' }}
                                                        onFocus={(e) => e.target.style.borderColor = 'var(--brand-blue)'}
                                                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                                        onWheel={(e) => e.target.blur()}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Action buttons */}
                                        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {entry.submitted ? (
                                                <ActionIcon
                                                    variant="filled"
                                                    color="green"
                                                    radius="md"
                                                    size="lg"
                                                    disabled
                                                >
                                                    <IconCheck size={16} stroke={2} />
                                                </ActionIcon>
                                            ) : (
                                                <>
                                                    <ActionIcon
                                                        variant={entry.confirmed ? 'filled' : 'default'}
                                                        color={entry.confirmed ? 'brand' : 'gray'}
                                                        radius="md"
                                                        size="lg"
                                                        onClick={() => toggleConfirm(entry.id)}
                                                        title={entry.confirmed ? 'Unconfirm' : 'Confirm'}
                                                    >
                                                        <IconCheck size={16} stroke={2} color={entry.confirmed ? 'white' : '#6b7280'} />
                                                    </ActionIcon>
                                                    <ActionIcon
                                                        variant="default"
                                                        radius="md"
                                                        size="lg"
                                                        onClick={() => removeEntry(entry.id)}
                                                        title="Remove"
                                                        style={{ borderColor: '#fecaca', color: '#dc2626' }}
                                                    >
                                                        <IconX size={16} stroke={2} color="#dc2626" />
                                                    </ActionIcon>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Submit bar */}
                    {pendingCount > 0 && (
                        <div style={{
                            position: 'sticky',
                            bottom: '20px',
                            backgroundColor: 'white',
                            border: '1.5px solid #e5e7eb',
                            borderRadius: '16px',
                            padding: '16px 20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                        }}>
                            <div style={{ fontSize: '14px', color: '#374151' }}>
                                <span style={{ fontWeight: '600' }}>{confirmedEntries.length}</span> of {pendingCount} confirmed
                            </div>
                            <button
                                onClick={handleSubmitAll}
                                disabled={!canSubmit}
                                style={{
                                    backgroundColor: canSubmit ? 'var(--brand-blue)' : '#e5e7eb',
                                    color: canSubmit ? 'white' : '#9ca3af',
                                    border: 'none',
                                    borderRadius: '10px',
                                    padding: '12px 28px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    cursor: canSubmit ? 'pointer' : 'not-allowed',
                                    transition: 'background-color 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                        Submitting…
                                    </>
                                ) : (
                                    <>Submit {confirmedEntries.length > 0 ? confirmedEntries.length : ''} Drive-Off{confirmedEntries.length !== 1 ? 's' : ''}</>
                                )}
                            </button>
                        </div>
                    )}

                    {/* Empty state */}
                    {entries.length === 0 && (
                        <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0', fontSize: '14px' }}>
                            Upload receipt photos above to get started
                        </div>
                    )}
                </div>
            </div>
        </div>
        </>
    );
};

export default ManualEntryPage;
